/**
 * Etykiety znaczników KSeF/JPK faktury zakupu.
 *
 * Kody trzyma `packages/shared/src/ksef-jpk-markers.ts` (jedno źródło prawdy wspólne z
 * CHECK-ami w bazie i walidatorami tras), a tłumaczenia żyją tutaj.
 *
 * `t()` z LITERAŁEM per gałąź, nie szablonem ze zmienną — klucze budowane dynamicznie są
 * martwym polem skanera `check:i18n`, czyli bramka nie zauważy braku tłumaczenia. Ten sam
 * powód co w `vatTaxCategoryLabel` obok. NIE refaktoryzować na lookup po kodzie.
 *
 * Nieznany kod zwracamy SUROWO, nie jako „—": w bazie jest CHECK, więc nieznana wartość
 * oznacza rozjazd kodu z bazą i lepiej, żeby był widoczny, niż ukryty pod myślnikiem.
 */

import { JPK_DOCUMENT_MARKERS, PURCHASE_EVIDENCE_MARKERS } from '@saas/shared';

import {
  codeFilterOptions,
  type CoreColumnTranslateFn,
  type ListFilterOption,
} from '@/lib/ext-list-columns';

// Funkcja tłumacząca: wspólny typ `CoreColumnTranslateFn` — świadomie NIE `TFunction` z i18next
// (silnik list podaje węższy typ, a pełne `TFunction` się z nim nie zgadza); przyjmuje oba
// wywołania — z formularza i z kolumny listy.

export function purchaseEvidenceMarkerLabel(code: string, t: CoreColumnTranslateFn): string {
  switch (code) {
    case 'NrKSeF':
      return t('tenant.purchaseInvoices.ksef.evidenceMarker.NrKSeF');
    case 'OFF':
      return t('tenant.purchaseInvoices.ksef.evidenceMarker.OFF');
    case 'BFK':
      return t('tenant.purchaseInvoices.ksef.evidenceMarker.BFK');
    case 'DI':
      return t('tenant.purchaseInvoices.ksef.evidenceMarker.DI');
    default:
      return code;
  }
}

export function jpkDocumentMarkerLabel(code: string, t: CoreColumnTranslateFn): string {
  switch (code) {
    case 'MK':
      return t('tenant.purchaseInvoices.ksef.jpkMarker.MK');
    case 'VAT_RR':
      return t('tenant.purchaseInvoices.ksef.jpkMarker.VAT_RR');
    case 'WEW':
      return t('tenant.purchaseInvoices.ksef.jpkMarker.WEW');
    default:
      return code;
  }
}

/**
 * Opcje listy wyboru „Znacznik ewidencji zakupu" w wierszu filtrów listy faktur: wartość = kod z bazy
 * (zamknięty zbiór ze `@saas/shared`), etykieta z `purchaseEvidenceMarkerLabel` — ta sama, co w komórce.
 */
export function purchaseEvidenceMarkerFilterOptions(t: CoreColumnTranslateFn): ListFilterOption[] {
  return codeFilterOptions(PURCHASE_EVIDENCE_MARKERS, purchaseEvidenceMarkerLabel, t);
}

/** Opcje listy wyboru „Oznaczenie dokumentu w JPK" — jak `purchaseEvidenceMarkerFilterOptions`. */
export function jpkDocumentMarkerFilterOptions(t: CoreColumnTranslateFn): ListFilterOption[] {
  return codeFilterOptions(JPK_DOCUMENT_MARKERS, jpkDocumentMarkerLabel, t);
}

export function ksefIssueModeLabel(code: string, t: CoreColumnTranslateFn): string {
  switch (code) {
    case 'online':
      return t('tenant.purchaseInvoices.ksef.issueMode.online');
    case 'offline24':
      return t('tenant.purchaseInvoices.ksef.issueMode.offline24');
    case 'offline':
      return t('tenant.purchaseInvoices.ksef.issueMode.offline');
    case 'awaryjny':
      return t('tenant.purchaseInvoices.ksef.issueMode.awaryjny');
    default:
      return code;
  }
}

export function documentReceiptChannelLabel(code: string, t: CoreColumnTranslateFn): string {
  switch (code) {
    case 'KSEF':
      return t('tenant.purchaseInvoices.ksef.receiptChannel.KSEF');
    case 'EMAIL_PDF':
      return t('tenant.purchaseInvoices.ksef.receiptChannel.EMAIL_PDF');
    case 'SKAN':
      return t('tenant.purchaseInvoices.ksef.receiptChannel.SKAN');
    case 'EDI':
      return t('tenant.purchaseInvoices.ksef.receiptChannel.EDI');
    case 'RECZNIE':
      return t('tenant.purchaseInvoices.ksef.receiptChannel.RECZNIE');
    default:
      return code;
  }
}
